import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import heroAvif from "@/assets/hero-hospital-corridor.png?format=avif&w=1920&quality=55";
import heroWebp from "@/assets/hero-hospital-corridor.png?format=webp&w=1920&quality=72";
import heroFallback from "@/assets/hero-hospital-corridor.png?format=jpg&w=1600&quality=72";

import { partners } from "@/data/partners";
import { localPartnerLogos } from "@/data/partnerLogosLocal";
import { listActivePartnerLogos, type PartnerLogo } from "@/lib/partnerLogos";
import { cn } from "@/lib/utils";
import { useHeroVideo } from "@/hooks/useHeroVideo";
import HeroVideoCard from "./HeroVideoCard";
import MegaMenu from "./nav/MegaMenu";
import MegaUtilityBar from "./nav/MegaUtilityBar";
import { megaNav } from "./nav/megaNavConfig";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import logoHorizontal from "@/assets/logo-horizontal.svg";

const ease = [0.22, 1, 0.36, 1] as const;

type LogoItem =
  | { kind: "wordmark"; name: string }
  | { kind: "logo"; name: string; url: string; scale?: number };

/* ---------------- Header inside hero ---------------- */

const HeaderInsideHero = ({
  sentinelRef,
}: {
  sentinelRef: React.RefObject<HTMLDivElement>;
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (href: string) =>
    href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(href + "/");

  const closeSheet = () => setOpen(false);

  return (
    <header
      className={cn(
        "z-50 transition-colors duration-300",
        scrolled
          ? "fixed top-0 inset-x-0 bg-white/95 backdrop-blur-md text-gray-900 shadow-sm"
          : "absolute top-0 inset-x-0 bg-transparent text-white",
      )}
    >
      <div className="flex items-center justify-between h-[72px] px-6 md:px-10">
        <Link
          to="/"
          aria-label="deepc home"
          className="inline-flex items-center text-current"
        >
          <img
            src={logoHorizontal}
            alt="deepc logo"
            width={120}
            height={24}
            className="h-[20px] md:h-[24px] w-auto"
            style={{ filter: scrolled ? "invert(1)" : undefined }}
          />
        </Link>

        <nav className="hidden lg:flex">
          <MegaMenu items={megaNav} dark={!scrolled} isActive={isActive} />
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="https://portal.marketplace.deepc.pro/analytics"
            target="_blank"
            rel="noreferrer"
            className={cn(
              "hidden lg:inline-flex items-center gap-2 rounded-md h-9 px-4 text-[13px] font-medium transition-colors",
              scrolled
                ? "text-gray-900 hover:bg-foreground/5 border border-gray-300"
                : "text-white border border-white/40 hover:bg-white/10",
            )}
          >
            Login
          </a>
          <Link
            to="/contact"
            className={cn(
              "hidden sm:inline-flex items-center gap-2 rounded-md h-9 px-4 text-[13px] font-medium transition-colors",
              scrolled
                ? "bg-primary text-white hover:bg-[hsl(var(--primary-hover))]"
                : "bg-transparent text-white border border-white/40 hover:bg-white/10",
            )}
          >
            Talk to our team
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              aria-label="Open menu"
              className={cn(
                "lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md text-current transition-colors",
                scrolled ? "hover:bg-foreground/5" : "hover:bg-white/10",
              )}
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-seabed text-seabed-foreground border-seabed-line p-0 w-[90%] max-w-sm flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-16 border-b border-seabed-line/70 shrink-0">
                <img src={logoHorizontal} alt="deepc logo" className="h-8 w-auto" />
                <SheetClose
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center h-10 w-10 rounded-md text-seabed-foreground/80 hover:bg-seabed-foreground/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-seabed"
                >
                  <X className="h-5 w-5" />
                </SheetClose>
              </div>
              <div className="flex-1 overflow-y-auto px-4 py-4">
                <Accordion type="single" collapsible className="w-full">
                  {megaNav.map((section) => (
                    <AccordionItem
                      key={section.href}
                      value={section.href}
                      className="border-seabed-line/40"
                    >
                      <AccordionTrigger className="text-[16px] font-display text-seabed-foreground hover:no-underline py-4">
                        {section.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col gap-1 pb-2">
                          {(() => {
                            const links = section.groups.flatMap((g) => g.links);
                            if (section.label === "Solution") {
                              const order = ["Build", "Deploy", "Govern", "Architecture", "Integrations"];
                              return [...links].sort((a, b) => {
                                const ai = order.indexOf(a.label);
                                const bi = order.indexOf(b.label);
                                return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
                              });
                            }
                            return links;
                          })().map((l) => (
                            <Link
                              key={l.href}
                              to={l.href}
                              onClick={closeSheet}
                              className="flex items-start gap-3 rounded-md p-2 hover:bg-seabed-foreground/[0.05]"
                            >
                              {l.icon && (
                                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                                  <l.icon className="h-3.5 w-3.5" />
                                </span>
                              )}
                              <span>
                                <span className="block text-[14px] font-medium text-seabed-foreground">
                                  {l.label}
                                </span>
                                <span className="block text-[12px] text-seabed-foreground/65 leading-snug">
                                  {l.description}
                                </span>
                              </span>
                            </Link>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <div className="mt-6">
                  <Link
                    to="/contact"
                    onClick={closeSheet}
                    data-theme="dark"
                    className="cta-primary w-full"
                  >
                    Talk to our team
                    <ArrowUpRight className="cta-arrow h-4 w-4" />
                  </Link>
                </div>
              </div>
              <MegaUtilityBar compact onNavigate={closeSheet} />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

/* ---------------- Hero ---------------- */

const HeroV2 = () => {
  const reduce = useReducedMotion() ?? false;
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [logos, setLogos] = useState<PartnerLogo[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    listActivePartnerLogos()
      .then((rows) => {
        if (!cancelled) setLogos(rows);
      })
      .catch(() => {
        if (!cancelled) setLogos([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items: LogoItem[] =
    logos && logos.length > 0
      ? logos
          .filter((l) => l.logo_url)
          .map((l) => ({ kind: "logo" as const, name: l.name, url: l.logo_url! }))
      : (() => {
          const logoNames = new Set(localPartnerLogos.map((l) => l.name));
          const logoItems = localPartnerLogos.map(
            (l) => ({ kind: "logo" as const, name: l.name, url: l.src, scale: l.scale }),
          );
          const wordmarkItems = partners
            .filter((name) => !logoNames.has(name))
            .map((name) => ({ kind: "wordmark" as const, name }));
          return [...logoItems, ...wordmarkItems];
        })();

  const loop = [...items, ...items];
  const { data: video } = useHeroVideo();

  return (
    <section className="bg-seabed">
     <div className="mx-auto">
      <div className="relative">
        {/* Sentinel observed by IntersectionObserver — top of card */}
        <div
          ref={sentinelRef}
          aria-hidden
          className="absolute top-0 left-0 right-0 h-px pointer-events-none"
        />

        {/* Header sits on top of the hero; transparent → fixed white on scroll */}
        <HeaderInsideHero sentinelRef={sentinelRef} />

        {/* hero-inner: overflow-hidden, holds background image + content */}
        <div className="relative overflow-hidden bg-seabed text-seabed-foreground min-h-[calc(100svh-var(--banner-h,0px))] lg:min-h-[calc(100dvh-var(--banner-h,0px))] flex flex-col">
          <picture>
            <source srcSet={heroAvif} type="image/avif" />
            <source srcSet={heroWebp} type="image/webp" />
            <img
              src={heroFallback}
              alt=""
              aria-hidden
              width={1920}
              height={1080}
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover object-center pointer-events-none select-none brightness-[0.65]"
            />
          </picture>

          {/* Mobile: full seabed wash so text stays legible */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--seabed) / 0.88) 0%, hsl(var(--seabed) / 0.78) 45%, hsl(var(--seabed) / 0.94) 100%)",
            }}
          />
          {/* Tablet: stronger dark left, photo peeks on the right edge */}
          <div
            className="absolute inset-0 pointer-events-none hidden md:block lg:hidden"
            aria-hidden
            style={{
              background:
                "linear-gradient(90deg, hsl(var(--seabed) / 0.96) 0%, hsl(var(--seabed) / 0.92) 55%, hsl(var(--seabed) / 0.55) 80%, hsl(var(--seabed) / 0.1) 100%)",
            }}
          />
          {/* Desktop: dark-left → transparent-right so the photo reads clearly on the right */}
          <div
            className="absolute inset-0 pointer-events-none hidden lg:block"
            aria-hidden
            style={{
              background:
                "linear-gradient(90deg, hsl(var(--seabed) / 1) 0%, hsl(var(--seabed) / 0.98) 40%, hsl(var(--seabed) / 0.6) 65%, hsl(var(--seabed) / 0) 100%)",
            }}
          />
          {/* Subtle bottom shade so the partner strip stays readable on desktop */}
          <div
            className="absolute inset-x-0 bottom-0 h-40 pointer-events-none hidden md:block"
            aria-hidden
            style={{
              background:
                "linear-gradient(180deg, hsl(var(--seabed) / 0) 0%, hsl(var(--seabed) / 0.7) 100%)",
            }}
          />

          {/* Video on: calm the photo on the right so the card reads clearly */}
          {video && (
            <div
              className="absolute inset-0 pointer-events-none hidden lg:block"
              aria-hidden
              style={{
                background:
                  "linear-gradient(90deg, hsl(var(--seabed) / 0) 35%, hsl(var(--seabed) / 0.55) 70%, hsl(var(--seabed) / 0.7) 100%)",
              }}
            />
          )}

          <div className="hero-overlay-glow" aria-hidden />
          

          <div className="container relative z-10 flex-1 flex flex-col justify-center pt-[120px] md:pt-[112px] lg:pt-[112px] pb-[128px] md:pb-[112px] lg:pb-[128px] text-center lg:text-left">
            <div className={video ? "lg:grid lg:grid-cols-12 lg:gap-12 lg:items-center" : undefined}>
            <div className={cn("max-w-[560px] mx-auto md:max-w-[640px] lg:mx-0 lg:max-w-[640px]", video && "lg:col-span-6")}>


              <motion.h1
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease, delay: 0.22 }}
                style={{ textWrap: "balance" } as React.CSSProperties}
                className="font-display tracking-tight text-seabed-foreground text-hero"
              >
                Healthcare needs infrastructure built for{" "}
                <span className="relative inline-block whitespace-nowrap">
                  clinical AI
                  <svg
                    aria-hidden
                    viewBox="0 0 300 12"
                    preserveAspectRatio="none"
                    className="pointer-events-none absolute left-0 right-0 -bottom-2 h-2 w-full overflow-visible"
                  >
                    <defs>
                      <linearGradient id="hero-underline-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="white" stopOpacity="0.3" />
                        <stop offset="50%" stopColor="white" stopOpacity="1" />
                        <stop offset="100%" stopColor="white" stopOpacity="0.3" />
                      </linearGradient>
                    </defs>
                    <path
                      d="M2 8 Q150 0 298 8"
                      fill="none"
                      stroke="url(#hero-underline-grad)"
                      strokeWidth="4"
                      strokeLinecap="round"
                      style={{ filter: "drop-shadow(0 0 6px rgba(255,255,255,0.5))" }}
                    />
                  </svg>
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease, delay: 0.38 }}
                className="mt-10 md:mt-6 lg:mt-10 max-w-[600px] mx-auto lg:mx-0 text-base md:text-lg text-seabed-foreground/80 leading-relaxed"
              >
                deepc connects once to clinical systems, giving health systems the foundation to build, deploy, and govern AI workflows safely at scale.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease, delay: 0.54 }}
                className="mt-14 md:mt-8 lg:mt-14 flex flex-col sm:flex-row items-center lg:justify-start justify-center gap-4 lg:gap-5"
              >
                <Link to="/contact" data-theme="dark" className="cta-primary group">
                  Talk to our team
                  <ArrowUpRight className="cta-arrow h-4 w-4" />
                </Link>
                <Link
                  to="/solution"
                  data-theme="dark"
                  className="cta-ghost group"
                >
                  Explore the solution
                  <span aria-hidden className="cta-arrow">→</span>
                </Link>
              </motion.div>
            </div>
            {video && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease, delay: 0.4 }}
                className="mt-12 lg:mt-0 lg:col-span-6 w-full lg:max-w-[640px] mx-auto lg:mr-0 text-left"
              >
                <HeroVideoCard video={video} />
              </motion.div>
            )}
            </div>
          </div>


          {/* Partner logo strip at bottom of card */}
          <div className="group absolute bottom-0 inset-x-0 z-10">
            <div
              className="overflow-hidden py-4 md:py-3 lg:py-5"
              style={{
                maskImage:
                  "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
              }}
            >
              <div
                className="flex w-max items-center marquee-track"

                style={{ animationDuration: reduce ? "0s" : "60s" }}
              >
                {loop.map((item, i) =>
                  item.kind === "logo" ? (
                    <div
                      key={`${item.name}-${i}`}
                      className="group flex box-content px-3 lg:px-5 w-[120px] md:w-[140px] lg:w-[160px] shrink-0 flex-col items-center justify-center gap-1.5"
                    >
                      <img
                        src={item.url}
                        alt={item.name}
                        title={item.name}
                        loading="lazy"
                        width={140}
                        height={32}
                        className="block h-7 md:h-8 w-auto max-w-[100px] md:max-w-[120px] lg:max-w-[140px] object-contain opacity-70"
                        style={item.scale ? { transform: `scale(${item.scale})` } : undefined}
                      />

                      <span className="hidden md:block w-full max-w-full font-display text-[11px] md:text-[12px] tracking-tight whitespace-nowrap text-seabed-foreground/60 text-center truncate opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        {item.name}
                      </span>
                    </div>
                  ) : (
                    <span
                      key={`${item.name}-${i}`}
                      className="flex box-content px-3 lg:px-5 w-[120px] md:w-[140px] lg:w-[160px] shrink-0 items-center justify-center text-center font-display text-[14px] md:text-[15px] tracking-tight whitespace-nowrap text-seabed-foreground/70 hover:text-seabed-foreground transition-colors truncate"
                    >
                      {item.name}
                    </span>
                  ),
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
     </div>
    </section>
  );
};

export default HeroV2;
